import { useEffect, useRef, useState } from 'react';

import { Autocomplete, Box, Chip, ListItemIcon, ListItemText, MenuItem, Select, Switch, TextField as MuiTextField, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';

import { ParameterMetadata, ParameterType, ParameterValueOption, PrimitiveType } from '../../../api/api';
import { SIMPLE_PRIMITIVE_TYPES } from '../../../common/applyObjectDefaultParameters';
import { ParameterTypes } from '@@common';
import { ExpressionInput } from '../Components/Expressions/ExpressionInput';
import CronExpressionInput from '../Components/Inputs/CronExpressionInput';
import EnvironmentIdsInput from '../Components/Inputs/EnvironmentIdsInput';
import EnumerationInput from '../Components/Inputs/EnumerationInput';
import ContentTemplateInput from '../Components/Inputs/ContentTemplateInput';
import FlowInput from '../Components/Inputs/FlowInput';
import FileIdInput from '../Components/Inputs/FileIdInput';
import GuidInput from '../Components/Inputs/GuidInput';
import IntegrationEventInput from '../Components/Inputs/IntegrationEventInput';
import OperationInput from '../Components/Inputs/OperationInput';
import PrimitiveListInput from '../Components/Inputs/PrimitiveListInput';
import SchemaInput from '../Components/Inputs/SchemaInput';
import SchemaValueInput from '../Components/Inputs/SchemaValueInput';
import { ServiceConnectionInput } from '../Components/Inputs/ServiceConnectionInput';
import TimeSpanInput from '../Components/Inputs/TimeSpanInput';
import StringMapInput from '../Components/Inputs/StringMapInput';
import { ConstExpressionModel } from '../Models/ConstExpressionModel';
import { EDGE_TYPE_OPTIONS } from '../Models/EdgeTypes';
import { ExpressionTypes } from '../Models/ExpressionTypes';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import {
	compareValueOptions,
	getValueOptionKey,
	getValueOptionLabel,
	hasValueOption,
	isWildcardValue,
	sortSelectedValues,
	useParameterValueOptions,
} from '../Services/parameterValueOptions';
import PropertyValueMultiLineInput from './PropertyValueMultiLineInput';
import { hasDifferentItems, normalizeValueOptionValue, normalizeValueOptionValues } from './valueOptions';

type PropertyValueInputProps = {
	parameterMetadata: ParameterMetadata;
	/// The value of the property
	value: any;
	/// Callback when the value changes
	onValueChange: (value: any) => void;
	variableScope?: VariableScope;

	/// All the parameters of the activity (needed to resolve typeRef → expectedObjectType)
	parameters?: ParameterMetadata[];
	/// The model (of the activity) that contains the property value (needed for typeRef resolution)
	model?: Record<string, any>;
	readOnly?: boolean;
	/// Optional custom renderer for parameter types. If returns non-null, uses that instead of default handling
	children?: (parameterMetadata: ParameterMetadata, value: any, onValueChange: (value: any) => void) => React.ReactNode;
};

function getStringArrayValue(value: any): string[] {
	if (!Array.isArray(value)) {
		return [];
	}

	return value.map((item) => String(item));
}

type StringTextFieldProps = {
	value: any;
	onValueChange: (value: any) => void;
	readOnly?: boolean;
};

function StringTextField({ value, onValueChange, readOnly }: StringTextFieldProps) {
	const [draft, setDraft] = useState(value ?? '');
	const isFocusedRef = useRef(false);

	useEffect(() => {
		if (!isFocusedRef.current) {
			setDraft(value ?? '');
		}
	}, [value]);

	return (
		<TextField
			variant="standard"
			required
			value={draft}
			disabled={readOnly}
			onFocus={() => {
				isFocusedRef.current = true;
			}}
			onChange={(e) => setDraft(e.target.value)}
			onBlur={() => {
				isFocusedRef.current = false;
				if (draft !== (value ?? '')) {
					onValueChange(draft);
				}
			}}
			fullWidth
		/>
	);
}

export default function PropertyValueInput({
	parameterMetadata,
	variableScope,
	value,
	onValueChange,
	parameters,
	model,
	readOnly,
	children,
}: PropertyValueInputProps) {
	const {
		valueOptions,
		valueOptionsSource,
		hasServiceConnectionPropertyOptionsSource,
		hasLoadedValueOptionsSource,
		serviceConnectionId,
		missingDependentValueNameRef,
	} = useParameterValueOptions(parameterMetadata, model);
	const isUnknownValue = (selectedValue: unknown) =>
		selectedValue !== null
		&& selectedValue !== undefined
		&& selectedValue !== ''
		&& !isWildcardValue(selectedValue)
		&& hasLoadedValueOptionsSource
		&& !hasValueOption(valueOptions, selectedValue);

	useEffect(() => {
		if (readOnly) {
			return;
		}

		if (parameterMetadata.type !== ParameterType.Primitive || valueOptions.length === 0 || value === null || value === undefined) {
			return;
		}

		if (parameterMetadata.isCollection) {
			if (!Array.isArray(value)) {
				return;
			}

			const normalizedValues = normalizeValueOptionValues(valueOptions, value);
			if (hasDifferentItems(value, normalizedValues)) {
				onValueChange?.(normalizedValues);
			}

			return;
		}

		const normalizedValue = normalizeValueOptionValue(valueOptions, value);
		if (normalizedValue !== value) {
			onValueChange?.(normalizedValue);
		}
	}, [parameterMetadata.isCollection, parameterMetadata.type, onValueChange, readOnly, value, valueOptions]);

	// Try custom renderer first (after all hooks have been called)
	const customRender = children?.(parameterMetadata, value, onValueChange);
	if (customRender !== null && customRender !== undefined) {
		return <>{customRender}</>;
	}
	if (parameterMetadata.typeCustom === ParameterTypes.StringMap) {
		return <StringMapInput value={value} onValueChange={onValueChange} readOnly={readOnly}
			title={parameterMetadata.displayName || parameterMetadata.name} optional={!parameterMetadata.isRequired} />;
	}

	if (parameterMetadata.type === ParameterType.Expression) {
		const isPrimitiveValue = value !== null && value !== undefined && typeof value !== 'object';
		const expressionValue =
			isPrimitiveValue && SIMPLE_PRIMITIVE_TYPES.has(parameterMetadata.expectedType?.type as PrimitiveType)
				? ({ Type: ExpressionTypes.Const, Value: value } as ConstExpressionModel)
				: value;
		return (
			<ExpressionInput
				expression={expressionValue}
				onExpressionChange={onValueChange}
				parameterMetadata={parameterMetadata}
				variableScope={variableScope}
				disabled={readOnly}
				parameters={parameters}
				model={model}
			/>
		);
	}
	if (parameterMetadata.type === ParameterType.ServiceConnectionRef) {
		return (
			<ServiceConnectionInput
				serviceConnectionType={parameterMetadata.matchType}
				serviceConnectionCategory={parameterMetadata.matchCategory}
				serviceConnectionRef={value}
				onServiceConnectionRefChange={onValueChange}
				disabled={readOnly}
			/>
		);
	}
	if (parameterMetadata.type === ParameterType.SchemaRef) {
		return <SchemaInput schemaRef={value} onSchemaRefChange={onValueChange} isEditable={!readOnly} />;
	}
	if (parameterMetadata.type === ParameterType.SchemaValue) {
		return <SchemaValueInput schemaValue={value} onSchemaValueChange={onValueChange} disabled={readOnly} />;
	}
	if (parameterMetadata.type === ParameterType.OperationRef) {
		return <OperationInput operationRef={value} onOperationRefChange={onValueChange} isEditable={!readOnly} />;
	}
	if (parameterMetadata.type === ParameterType.IntegrationEventRef) {
		return <IntegrationEventInput integrationEventRef={value} onIntegrationEventRefChange={onValueChange} isEditable={!readOnly} />;
	}
	if (parameterMetadata.type === ParameterType.FlowRef) {
		return <FlowInput flowRef={value} onFlowRefChange={onValueChange} isEditable={!readOnly} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.EnumRef) {
		return <EnumerationInput enumRef={value} onEnumRefChange={onValueChange} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.ContentTemplateId) {
		return <ContentTemplateInput templateId={value} onTemplateIdChange={onValueChange} />;
	}
	if (parameterMetadata.typeCustom === ParameterTypes.FileId) {
		return <FileIdInput value={value} onValueChange={onValueChange} matchType={parameterMetadata.matchType} readOnly={readOnly} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.CronExpression) {
		return <CronExpressionInput value={value} onValueChange={onValueChange} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.EnvironmentIds) {
		return <EnvironmentIdsInput value={value} onValueChange={onValueChange} />;
	}

	if (parameterMetadata.type === ParameterType.Primitive) {
		if (valueOptions.length > 0) {
			if (parameterMetadata.isCollection) {
				const selectedValues = sortSelectedValues(valueOptions, getStringArrayValue(value));
				return (
					<Autocomplete
						multiple
						disableCloseOnSelect
						freeSolo={valueOptionsSource?.allowCustomValues ?? false}
						options={valueOptions.map((option) => String(option.value))}
						value={selectedValues}
						disabled={readOnly}
						onChange={(_, selected) => onValueChange?.(sortSelectedValues(valueOptions, selected.map(String)))}
						getOptionLabel={(option) => getValueOptionLabel(valueOptions, option)}
						renderTags={(selected, getTagProps) =>
							selected.map((option, index) => {
								const isUnknown = isUnknownValue(option);
								return (
									<Chip
										{...getTagProps({ index })}
										key={`${option}:${index}`}
										label={getValueOptionLabel(valueOptions, option)}
										size="small"
										color={isUnknown ? 'error' : 'default'}
										variant={isUnknown ? 'filled' : 'outlined'}
										title={isUnknown ? 'This value is no longer available from the selected service connection.' : undefined}
									/>
								);
							})
						}
						renderInput={(params) => <MuiTextField {...params} variant="standard" />}
					/>
				);
			}

			const selectedValue = normalizeValueOptionValue(valueOptions, value ?? parameterMetadata.defaultValue ?? (parameterMetadata.isRequired ? valueOptions[0].value : ''));
			const selectedValueIsUnknown = isUnknownValue(selectedValue);
			const selectableOptions = selectedValueIsUnknown
				? [
						...valueOptions,
						{
							name: String(selectedValue),
							description: 'This value is no longer available from the selected service connection.',
							value: selectedValue,
						} as ParameterValueOption,
					].sort(compareValueOptions)
				: valueOptions;

			return (
				<Select
					variant="standard"
					value={selectedValue}
					disabled={readOnly}
					onChange={(e) => onValueChange?.(e.target.value === '' ? null : e.target.value)}
					fullWidth
					error={selectedValueIsUnknown}
					renderValue={(selected) => {
						const option = valueOptions.find((valueOption) => String(valueOption.value) === String(selected));
						return (
							<Box component="span" sx={{ color: selectedValueIsUnknown ? 'error.main' : undefined }}>
								{option?.name ?? String(selected)}
							</Box>
						);
					}}
				>
					{!parameterMetadata.isRequired && (
						<MenuItem value="">
							<ListItemText sx={{ fontStyle: 'italic', color: 'text.secondary' }}>None</ListItemText>
						</MenuItem>
					)}
					{selectableOptions.map((option) => (
						<MenuItem key={getValueOptionKey(option)} value={option.value} title={option.description}>
							<ListItemText sx={{ color: selectedValueIsUnknown && String(option.value) === String(selectedValue) ? 'error.main' : undefined }}>
								{option.name}
							</ListItemText>
						</MenuItem>
					))}
				</Select>
			);
		}

		if (hasServiceConnectionPropertyOptionsSource && !serviceConnectionId) {
			return (
				<Typography variant="body2" color="text.secondary">
					Select a service connection first.
				</Typography>
			);
		}

		if (hasServiceConnectionPropertyOptionsSource && missingDependentValueNameRef) {
			return (
				<Typography variant="body2" color="text.secondary">
					Set {missingDependentValueNameRef} first.
				</Typography>
			);
		}

		// Handle collection of primitives
		if (parameterMetadata.isCollection) {
			if (parameterMetadata.expectedType?.type === PrimitiveType.String) {
				return <PrimitiveListInput type="string" value={value} onValueChange={onValueChange} readOnly={readOnly} />;
			}
			if (parameterMetadata.expectedType?.type === PrimitiveType.Integer) {
				return <PrimitiveListInput type="integer" value={value} onValueChange={onValueChange} readOnly={readOnly} />;
			}
		}

		if (parameterMetadata.expectedType?.type === PrimitiveType.String) {
			// Check if this is the EdgeType parameter
			// ToDo: improve this by adding options in ParameterMetadata
			if (parameterMetadata.name === 'Metadata.edgeType') {
				return (
					<Select
					variant="standard"
					value={value ?? EDGE_TYPE_OPTIONS[0].value}
					disabled={readOnly}
					onChange={(e) => onValueChange?.(e.target.value)}
						fullWidth
						renderValue={(selected) => {
							const option = EDGE_TYPE_OPTIONS.find((opt) => opt.value === selected);
							return (
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{option?.icon()}
									<span>{option?.label}</span>
								</div>
							);
						}}
					>
						{EDGE_TYPE_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								<ListItemIcon sx={{ minWidth: 32 }}>{option.icon()}</ListItemIcon>
								<ListItemText>{option.label}</ListItemText>
							</MenuItem>
						))}
					</Select>
				);
			}
			if (parameterMetadata.isStringMultiLine) {
				return <PropertyValueMultiLineInput value={value} onValueChange={onValueChange} label={parameterMetadata.displayName} required readOnly={readOnly} />;
			}
			return <StringTextField value={value} onValueChange={onValueChange} readOnly={readOnly} />;
		}
		if (parameterMetadata.expectedType?.type === PrimitiveType.Boolean) {
			return <Switch checked={value === true} onChange={(e) => onValueChange?.(e.target.checked)} disabled={readOnly} size="small" />;
		}
		if (parameterMetadata.expectedType?.type === PrimitiveType.Integer) {
			return (
				<TextField
					variant="standard"
					type="number"
					required
					value={value ?? ''}
					disabled={readOnly}
					onChange={(e) => {
						const parsed = parseInt(e.target.value, 10);
						onValueChange?.(isNaN(parsed) ? null : parsed);
					}}
					fullWidth
				/>
			);
		}
		if (parameterMetadata.expectedType?.type === PrimitiveType.TimeSpan) {
			return <TimeSpanInput value={value} onValueChange={onValueChange} optional={!parameterMetadata.isRequired} disabled={readOnly}
				emptyLabel={parameterMetadata.name === 'ConversationExpiration' ? 'No expiration' : 'Not set'} />;
		}
		if (parameterMetadata.expectedType?.type === PrimitiveType.Guid) {
			return <GuidInput value={value} onValueChange={(val) => onValueChange?.(val)} />;
		}
	}
	return <>ToDo: Unknown Type</>;
}
